/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import downloadjs from 'downloadjs';
import SidePanelContainer from '../controls/SidePanelContainer';
import {
  DeviceAttributesV1,
  DeviceType,
  ParametersType,
  PriorityList,
  TimeSpan,
  TimeSpanDateAttributes
} from '../constants/telosair-interfaces';
import { useUnifiedData } from '../hooks/useUnifiedData';
import {
  getPriorityColor,
  getActiveConfig,
  getThresholds
} from '../_helpers/dashboardHelper';
import { getDisplayableValue, getParameterName } from '../_helpers/globalHelper';
import { resolveStatus, isAqiParam, StatusInfo } from '../_helpers/airQuality';
import { STALE_HEX, isSummaryStale, lastReportedLabel } from '../_helpers/staleData';
import { getDeviceSummaryOverTime } from '../services/unified-dashboard-service';
import { resolveDeviceImage } from '../services/deviceImage.service';
import Deque from '../classes/deque';
import { clampResolution, getResolutionOptions } from '../_helpers/timeResolution';
import Graph from './histogram/Graph';
import DateRangePicker from '../controls/DateRangePicker';
import Loader from './loader';

type DeviceDetailPanelProps = {
  device: DeviceAttributesV1 | null;
  // Parameter to preselect for the chart (the metric the caller is already
  // showing, e.g. the map's legend selection). Falls back to AQI.
  initialParameter?: ParametersType | null;
  // Accepted for API compatibility with callers; the panel derives its own
  // range from the Today / Yesterday / 7d / 30d toggle.
  // eslint-disable-next-line react/no-unused-prop-types
  timeSpan?: TimeSpan;
  // eslint-disable-next-line react/no-unused-prop-types
  timeSpanDate?: { startDate: number; endDate: number };
  onClose: () => void;
};

// US EPA AQI scale segments (0-500) used for the gradient scale + ticks.
const AQI_SEGMENTS = [
  { color: '#38DC62' }, // 0-50   Good
  { color: '#FFD21A' }, // 51-100 Moderate
  { color: '#FF8C1A' }, // 101-150 Unhealthy for Sensitive
  { color: '#FF5245' }, // 151-200 Unhealthy
  { color: '#A21CAF' }, // 201-300 Very Unhealthy
  { color: '#7F1D1D' } // 301-500 Hazardous
];
const AQI_BOUNDS = [0, 50, 100, 150, 200, 300, 500];

// Status badge classes -> hex, so the header glow can be driven by the same
// status that colours the badges (used when the headline isn't AQI).
const BADGE_HEX: Record<string, string> = {
  'bg-success-500': '#38DC62',
  'bg-warning-500': '#FFAD1A',
  'bg-warning-600': '#C1851C',
  'bg-error-500': '#FF5245',
  'bg-error-700': '#A43A31',
  'bg-error-900': '#7F1D1D',
  'bg-gray-500': '#6B7280'
};

// Colours for the four threshold reference lines drawn on the chart.
const THRESHOLD_WARNING_HEX = '#FFAD1A';
const THRESHOLD_CRITICAL_HEX = '#FF5245';

// A reading older than this (or missing) is treated as stale and the headline
// metric is visibly demoted so we never present old data as live.
const STALE_AFTER_MIN = 60;

// Ranks priorities so we can surface the worst parameter as "Main concern".
const PRIORITY_RANK: Record<string, number> = {
  [PriorityList.critical]: 3,
  [PriorityList.high]: 3,
  [PriorityList.outOfRange]: 3,
  [PriorityList.medium]: 2,
  [PriorityList.low]: 1,
  [PriorityList.none]: 0
};

// Only surface a "Main concern" when a parameter is genuinely elevated
// (medium severity or worse) — never for a healthy reading.
const CONCERN_MIN_RANK = 2;

/**
 * Resolves the AQI category colour (hex) for a value, mirroring the scale.
 * @param v - The AQI value.
 * @returns The segment colour as a hex string.
 */
function aqiHex(v: number): string {
  for (let i = 0; i < AQI_SEGMENTS.length; i += 1) {
    if (v < AQI_BOUNDS[i + 1]) return AQI_SEGMENTS[i].color;
  }
  return AQI_SEGMENTS[AQI_SEGMENTS.length - 1].color;
}

type ChartRange = 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';

// Plain-language description shown under the AQI scale per EPA category.
const AQI_DESCRIPTIONS: { max: number; text: string }[] = [
  { max: 50, text: 'Air quality is satisfactory, and air pollution poses little or no risk.' },
  {
    max: 100,
    text: 'Air quality is acceptable. Some pollutants may be a concern for a very small number of unusually sensitive people.'
  },
  {
    max: 150,
    text: 'Members of sensitive groups may experience health effects. The general public is less likely to be affected.'
  },
  {
    max: 200,
    text: 'Some members of the general public may experience health effects; sensitive groups may experience more serious effects.'
  },
  {
    max: 300,
    text: 'Health alert: the risk of health effects is increased for everyone. Limit prolonged outdoor exertion.'
  },
  {
    max: Infinity,
    text: 'Health warning of emergency conditions: everyone is more likely to be affected. Avoid outdoor activity.'
  }
];

/**
 * Returns a plain-language summary for an AQI value.
 * @param v - The AQI value.
 * @returns A descriptive sentence for the matching category.
 */
function getAqiDescription(v: number): string {
  return (AQI_DESCRIPTIONS.find((d) => v <= d.max) || AQI_DESCRIPTIONS[AQI_DESCRIPTIONS.length - 1])
    .text;
}

// Full name + one-line explainer for each pollutant, shown beneath the grid
// when a parameter is selected.
const PARAM_INFO: Partial<Record<ParametersType, { full: string; desc: string }>> = {
  aqi: {
    full: 'Air Quality Index',
    desc: 'A combined score summarising overall air quality from multiple pollutants.'
  },
  pm25_m: {
    full: 'Fine Particulate Matter (PM2.5)',
    desc: 'Tiny airborne particles that can travel deep into the lungs and bloodstream.'
  },
  pm100_m: {
    full: 'Coarse Particulate Matter (PM10)',
    desc: 'Inhalable dust and particles from roads, construction, and pollen.'
  },
  o3: { full: 'Ozone', desc: 'A reactive gas formed in sunlight that can irritate the airways.' },
  no2: {
    full: 'Nitrogen Dioxide',
    desc: 'A combustion gas mainly from vehicles and the burning of fuel.'
  },
  co: {
    full: 'Carbon Monoxide',
    desc: 'An odourless gas from incomplete combustion that reduces oxygen delivery.'
  },
  co2: {
    full: 'Carbon Dioxide',
    desc: 'A marker of ventilation; high levels indicate stale, poorly ventilated air.'
  },
  tvoc: {
    full: 'Total Volatile Organic Compounds',
    desc: 'A mix of carbon-based gases emitted by paints, cleaning products, and furnishings.'
  },
  pn03_m: {
    full: 'Particle Number (0.3µm)',
    desc: 'The count of ultrafine particles per cubic centimetre of air.'
  },
  temp: { full: 'Temperature', desc: 'The ambient air temperature measured by the device.' },
  hum: {
    full: 'Relative Humidity',
    desc: 'The amount of moisture in the air relative to its maximum capacity.'
  },
  pressure: { full: 'Barometric Pressure', desc: 'The atmospheric pressure acting on the sensor.' },
  ch4: { full: 'Methane', desc: 'A flammable hydrocarbon and potent greenhouse gas.' },
  so2: {
    full: 'Sulphur Dioxide',
    desc: 'A sharp-smelling gas from burning fossil fuels that irritates the airways.'
  },
  ci: {
    full: 'Comfort Index',
    desc: 'A combined measure of thermal comfort from temperature and humidity.'
  }
};

/**
 * Maps an AQI value to its position (0-100%) across the segmented scale.
 * @param v - The AQI value.
 * @returns The marker offset percentage.
 */
function aqiToPercent(v: number): number {
  if (v <= 0) return 0;
  for (let i = 0; i < AQI_SEGMENTS.length; i += 1) {
    const lo = AQI_BOUNDS[i];
    const hi = AQI_BOUNDS[i + 1];
    if (v < hi) return ((i + (v - lo) / (hi - lo)) / AQI_SEGMENTS.length) * 100;
  }
  return 100;
}

/**
 * A slide-in panel showing a device's air-quality summary and a time-series
 * chart, modelled on a reference-grade air-quality monitor card.
 * @param props - The props for the panel.
 * @returns A React element representing the device detail panel.
 */
export default function DeviceDetailPanel(props: DeviceDetailPanelProps): ReactElement | null {
  const { device, initialParameter = null, onClose } = props;
  const { parameters, deviceDataSummary, clientThresholdSettings, userConfigDataV2, devices } =
    useUnifiedData();

  // Latest plotted [timestamp, value] pairs, lifted from the chart for CSV export.
  const [series, setSeries] = useState<number[][]>([]);

  const sortedParameters = useMemo(
    () => [...(parameters || [])].sort((a, b) => a.order - b.order),
    [parameters]
  );

  const aqiParameter = useMemo(
    () => sortedParameters.find((p) => isAqiParam(p.parameterName)) || sortedParameters[0] || null,
    [sortedParameters]
  );

  const [selectedParameter, setSelectedParameter] = useState<ParametersType | null>(null);
  const [chartRange, setChartRange] = useState<ChartRange>('today');
  // User-picked custom start/end range (epoch ms), used when chartRange === 'custom'.
  const [customRange, setCustomRange] = useState<TimeSpanDateAttributes | null>(null);
  const [showRangePicker, setShowRangePicker] = useState(false);
  // Selected parameter's [timestamp, value] series, keyed by parameter name so it
  // can drive the shared Time Series <Graph> the Simplified dashboard uses.
  const [graphData, setGraphData] = useState<Record<string, Deque>>({});
  const [chartLoading, setChartLoading] = useState(true);

  // Photo of the physical sensor, taken in the mobile app. Presigned and
  // short-lived, so it is fetched per open rather than cached.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoExpanded, setPhotoExpanded] = useState(false);
  // Threshold reference lines are on by default; they can be hidden because a
  // far-out bound stretches the y-axis and flattens the plotted series.
  const [showThresholds, setShowThresholds] = useState(true);

  const serialNumber = device?.serial_number;

  useEffect(() => {
    const preselected =
      initialParameter && sortedParameters.some((p) => p.parameterName === initialParameter)
        ? initialParameter
        : null;
    setSelectedParameter(preselected ?? aqiParameter?.parameterName ?? null);
  }, [aqiParameter, serialNumber, initialParameter, sortedParameters]);

  const deviceType =
    devices.find((d) => d.serialNumber === serialNumber)?.deviceType || DeviceType.MIXED;

  const chartParameter =
    sortedParameters.find((p) => p.parameterName === selectedParameter) || aqiParameter;

  /**
   * Normalises a reading for display. Values arrive from the API already
   * expressed in the user's selected unit (the backend applies the unit
   * config), so nothing is converted here.
   * @param raw - Value from the backend.
   * @returns The reading, or null when absent.
   */
  const getReading = (raw: number | null | undefined): number | null =>
    raw === null || raw === undefined ? null : raw;

  /**
   * Resolves the status (label + badge class) for a parameter value.
   * @param param - The parameter key.
   * @param value - The converted value.
   * @returns The resolved status.
   */
  const statusFor = (param: ParametersType, value: number | null): StatusInfo => {
    const priority = getPriorityColor(
      param,
      value,
      clientThresholdSettings,
      userConfigDataV2,
      deviceType
    );
    return resolveStatus(param, value, priority);
  };

  const chartParamName = chartParameter?.parameterName ?? null;

  // Resolve the chart's date range from the Today / Yesterday / 7d / 30d / Custom toggle.
  const chartTimeSpanDate = useMemo(() => {
    if (chartRange === 'custom' && customRange) return customRange;
    const now = moment();
    const startOfToday = now.clone().startOf('day');
    switch (chartRange) {
      case 'yesterday':
        return {
          startDate: startOfToday.clone().subtract(1, 'day').valueOf(),
          endDate: startOfToday.valueOf()
        };
      case 'last7':
        return { startDate: now.clone().subtract(7, 'day').valueOf(), endDate: now.valueOf() };
      case 'last30':
        return { startDate: now.clone().subtract(30, 'day').valueOf(), endDate: now.valueOf() };
      default:
        return { startDate: startOfToday.valueOf(), endDate: now.valueOf() };
    }
  }, [chartRange, customRange]);
  const chartTimeSpan =
    // eslint-disable-next-line no-nested-ternary
    chartRange === 'custom'
      ? TimeSpan.Custom
      : // eslint-disable-next-line no-nested-ternary
      chartRange === 'last7'
      ? TimeSpan.Week1
      : chartRange === 'last30'
      ? TimeSpan.Month1
      : TimeSpan.Hrs24;

  // The configured threshold bounds, drawn as horizontal reference lines across
  // the chart so a reading can be read against its alert levels.
  //
  // Only bounds that describe a real alert level are drawn. A bound is skipped
  // when it is absent from the active config, or when it sits at or below the
  // parameter's valid floor. That floor rule covers the two ways a bound comes
  // back unset: pollutants ship with lowerWarning/lowerCritical of 0, which is
  // not an alert level ("too clean" is not a thing), and a config saved without
  // values comes back with all four bounds at 0 — an upper bound of 0 would
  // claim every reading is critical. Both would otherwise draw a meaningless
  // dashed line along the x-axis. Nothing meaningful configured draws nothing.
  const thresholdAnnotations = useMemo(() => {
    if (!chartParamName) return {};
    const configured = getActiveConfig(clientThresholdSettings, deviceType).thresholds[
      chartParamName
    ];
    if (!configured || configured.incomplete) return {};
    const bounds = getThresholds(
      chartParamName,
      clientThresholdSettings,
      userConfigDataV2,
      deviceType
    );
    const floor = typeof bounds.min === 'number' && Number.isFinite(bounds.min) ? bounds.min : 0;
    const lines: { key: keyof typeof bounds; label: string; color: string }[] = [
      { key: 'lowerCritical', label: 'Lower critical', color: THRESHOLD_CRITICAL_HEX },
      { key: 'lowerWarning', label: 'Lower warning', color: THRESHOLD_WARNING_HEX },
      { key: 'upperWarning', label: 'Upper warning', color: THRESHOLD_WARNING_HEX },
      { key: 'upperCritical', label: 'Upper critical', color: THRESHOLD_CRITICAL_HEX }
    ];
    const result: Record<string, unknown> = {};
    const drawnAt = new Map<number, string>();
    lines.forEach((line) => {
      const value = bounds[line.key];
      if (typeof value !== 'number' || !Number.isFinite(value)) return;
      if (value <= floor) return;
      // Two bounds configured at the same value would stack their labels into
      // an unreadable smear; keep the first and note both names on it.
      const existing = drawnAt.get(value);
      if (existing) {
        const annotation = result[existing] as { label: { content: string } };
        annotation.label.content = `${annotation.label.content.split(' · ')[0]} / ${
          line.label
        } · ${getDisplayableValue(chartParamName, value)}`;
        return;
      }
      drawnAt.set(value, `threshold-${line.key}`);
      result[`threshold-${line.key}`] = {
        type: 'line',
        yMin: value,
        yMax: value,
        yScaleID: 'y',
        borderColor: line.color,
        borderWidth: 1.5,
        borderDash: [6, 4],
        drawTime: 'afterDatasetsDraw',
        // Widen the y-axis when a bound sits outside the plotted range, so
        // every configured line stays visible rather than being clipped away.
        adjustScaleRange: true,
        label: {
          display: true,
          content: `${line.label} · ${getDisplayableValue(chartParamName, value)}`,
          position: line.key.startsWith('lower') ? 'start' : 'end',
          backgroundColor: 'rgba(11,12,18,0.75)',
          color: line.color,
          font: { size: 11, weight: '500' },
          padding: { top: 2, bottom: 2, left: 5, right: 5 },
          borderRadius: 4,
          // Labels sit on the inward side of their line: lower bounds above,
          // upper bounds below. Pushing a lower bound's label down instead put
          // it past the bottom of the plot area whenever the line sat near the
          // axis, where the chart clipped it in half.
          yAdjust: line.key.startsWith('lower') ? -9 : 9
        }
      };
    });
    return result;
  }, [chartParamName, clientThresholdSettings, userConfigDataV2, deviceType]);

  useEffect(() => {
    if (!serialNumber) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setPhotoUrl(null);
    setPhotoExpanded(false);
    setPhotoLoading(true);
    resolveDeviceImage(serialNumber, controller.signal).then((url) => {
      if (cancelled) return;
      setPhotoUrl(url);
      setPhotoLoading(false);
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [serialNumber]);

  // Esc closes the expanded photo before it would close the panel itself.
  useEffect(() => {
    if (!photoExpanded) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setPhotoExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [photoExpanded]);

  // This panel owns its chart range, so it owns its resolution too: the width
  // is clamped to whatever the panel's own range allows.
  const [resolution, setResolution] = useState<string | null>(null);
  const chartResolution = clampResolution(
    resolution,
    chartTimeSpanDate.startDate,
    chartTimeSpanDate.endDate
  );

  // Fetch the selected device+parameter series and shape it as the
  // Record<parameter, Deque> the shared Time Series <Graph> expects.
  useEffect(() => {
    if (!serialNumber || !chartParamName) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setChartLoading(true);
    getDeviceSummaryOverTime(
      {
        startDate: chartTimeSpanDate.startDate,
        endDate: chartTimeSpanDate.endDate,
        devices: [serialNumber],
        dataPointsDifference: chartResolution
      },
      { signal: controller.signal }
    )
      .then((res) => {
        if (cancelled) return;
        const match = res?.data?.data?.find(
          (item: { name: string; data: number[][] }) => item.name === chartParamName
        );
        const points: number[][] = match?.data ?? [];
        const dq = new Deque();
        if (points.length) dq.push(...points);
        setGraphData({ [chartParamName]: dq });
        setSeries(points);
        setChartLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setGraphData({});
        setSeries([]);
        setChartLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [serialNumber, chartParamName, chartTimeSpanDate, chartResolution]);

  if (!device || !aqiParameter || !chartParameter) return null;

  const summary = deviceDataSummary[device.serial_number];
  const particleData = summary?.parameters || {};
  // The window held nothing for this device, so these are its last known readings.
  const summaryStale = isSummaryStale(summary);

  // Headline metric is always AQI (or the primary parameter when AQI is absent).
  const aqiValue = getReading(particleData[aqiParameter.parameterName]);
  const aqiStatus = statusFor(aqiParameter.parameterName, aqiValue);
  const headlineIsAqi = isAqiParam(aqiParameter.parameterName);

  /**
   * Resolves the display unit for a parameter (user preference first).
   * @param param - The parameter key.
   * @returns The unit string, or an empty string when unitless.
   */
  const unitFor = (param: ParametersType): string =>
    (userConfigDataV2?.config?.unit && userConfigDataV2.config.unit[param]) ||
    sortedParameters.find((p) => p.parameterName === param)?.unit ||
    '';

  // "Main concern": the worst-ranked non-AQI parameter with a reading.
  type Concern = { name: string; status: StatusInfo; value: string };
  // `as` cast keeps the flow type as Concern | null; assignments inside the
  // forEach below are invisible to TS narrowing otherwise.
  let mainConcern = null as Concern | null;
  let worstRank = 0;
  sortedParameters.forEach((p) => {
    if (isAqiParam(p.parameterName)) return;
    const v = getReading(particleData[p.parameterName]);
    if (v === null) return;
    const priority = getPriorityColor(
      p.parameterName,
      v,
      clientThresholdSettings,
      userConfigDataV2,
      deviceType
    );
    const rank = PRIORITY_RANK[priority] || 0;
    if (rank > worstRank) {
      worstRank = rank;
      const unit = unitFor(p.parameterName);
      mainConcern = {
        name: getParameterName(p, deviceType).shortName,
        status: resolveStatus(p.parameterName, v, priority),
        value: `${getDisplayableValue(p.parameterName, v)}${unit ? ` ${unit}` : ''}`
      };
    }
  });
  const showConcern = !!mainConcern && worstRank >= CONCERN_MIN_RANK;

  const markerPercent = aqiValue !== null && headlineIsAqi ? aqiToPercent(aqiValue) : null;

  // Status colour for the AQI headline + category label.
  const accentHex =
    headlineIsAqi && aqiValue !== null
      ? aqiHex(aqiValue)
      : BADGE_HEX[aqiStatus.badgeClass] || BADGE_HEX['bg-gray-500'];

  // Freshness: an old or missing reading shows the monitor as inactive.
  const lastSeenAt = device.lastSeen ? moment(device.lastSeen) : null;
  const minsSince = lastSeenAt ? moment().diff(lastSeenAt, 'minutes') : null;
  const isStale = summaryStale || minsSince === null || minsSince > STALE_AFTER_MIN;

  const placement = device.floorPlan === DeviceType.OUTDOOR ? 'Outdoor' : 'Indoor';

  const chartValue = getReading(particleData[chartParameter.parameterName]);
  const chartUnit = unitFor(chartParameter.parameterName);
  const selShort = getParameterName(chartParameter, deviceType).shortName;

  const hasThresholdLines = Object.keys(thresholdAnnotations).length > 0;

  // Pollutant cards exclude AQI (already the headline metric).
  const pollutantParameters = sortedParameters.filter((p) => !isAqiParam(p.parameterName));

  const RANGE_TABS: { id: ChartRange; label: string }[] = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'last7', label: 'Last 7 days' },
    { id: 'last30', label: 'Last 30 days' }
  ];

  /**
   * Exports the currently plotted series as a CSV download.
   */
  const downloadCsv = (): void => {
    if (!series.length) return;
    const rows = series
      .filter((pair) => Array.isArray(pair) && pair.length >= 2)
      .map(([ts, value]) => `${moment(ts).format('YYYY-MM-DD HH:mm')},${value}`);
    const csv = `timestamp,${selShort}\n${rows.join('\n')}`;
    downloadjs(csv, `${device.deviceName}-${selShort}-${chartRange}.csv`, 'text/csv');
  };

  const card = 'rounded border border-white/[0.06] bg-white/[0.02]';
  // One height and type scale for every control in the chart toolbar; they read
  // as a mismatched pile otherwise.
  const control =
    'h-9 rounded border text-sm font-medium transition-colors focus:border-[#8B5CF6] focus:outline-none';
  const controlIdle = 'border-white/[0.08] bg-white/[0.04]';
  const selectClass = `${control} ${controlIdle} appearance-none pl-3 pr-9 text-white`;
  const controlLabel = 'text-sm text-gray-400';
  const chevron =
    'material-icons-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-gray-400';

  // Same Time Series chart the Simplified dashboard renders, for the selected metric.
  let chartContent: ReactElement;
  if (chartLoading) {
    chartContent = (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  } else if (graphData[chartParameter.parameterName]?.size()) {
    chartContent = (
      <Graph<ParametersType>
        labels={{ [chartParameter.parameterName]: selShort } as Record<ParametersType, string>}
        lineChartGraphData={graphData as Record<ParametersType, Deque>}
        timeSpan={chartTimeSpan}
        type={deviceType}
        useDynamicLineColorPlugin
        useTextPointPlugin
        storageKey={undefined}
        annotations={showThresholds ? thresholdAnnotations : {}}
      />
    );
  } else {
    chartContent = (
      <div className="flex h-full items-center justify-center text-base font-medium text-gray-400">
        No Data Available
      </div>
    );
  }

  return (
    <SidePanelContainer
      width="w-full lg:w-[50vw]"
      isOpen={!!device}
      closeModal={onClose}
      // The map behind this panel must stay live: users pan/zoom it and click
      // another marker to switch devices without closing the panel first.
      allowBackgroundInteraction
    >
      <div className="pb-10 pt-6 text-white">
        {/* ===== Summary card ===== */}
        <section className={`${card} p-4`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3.5">
              {photoLoading && (
                <div className="h-20 w-20 shrink-0 animate-pulse rounded border border-white/[0.06] bg-white/[0.04]" />
              )}
              {!photoLoading && photoUrl && (
                <button
                  type="button"
                  onClick={() => setPhotoExpanded(true)}
                  aria-label={`View photo of ${device.deviceName}`}
                  className="group relative h-20 w-20 shrink-0 overflow-hidden rounded border border-white/[0.08] transition-colors hover:border-white/25"
                >
                  <img
                    src={photoUrl}
                    alt={`${device.deviceName} sensor`}
                    className="h-full w-full object-cover"
                    onError={() => setPhotoUrl(null)}
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="material-icons-outlined text-[20px] text-white">zoom_in</span>
                  </span>
                </button>
              )}
              <div className="min-w-0">
                <h2 className="truncate text-2xl font-semibold text-white">{device.deviceName}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-400">
                  <span>{placement}</span>
                  <span className="text-gray-600">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${
                        isStale ? 'bg-gray-500' : 'bg-success-500'
                      }`}
                    />
                    {isStale ? 'Inactive' : 'Active'}
                  </span>
                  <span className="text-gray-600">·</span>
                  <span>SN {device.serial_number}</span>
                </div>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="flex items-baseline justify-end gap-1.5">
                <span
                  className={`text-5xl font-bold leading-none ${isStale ? 'opacity-50' : ''}`}
                  style={{ color: accentHex }}
                >
                  {aqiValue !== null
                    ? getDisplayableValue(aqiParameter.parameterName, aqiValue)
                    : '--'}
                </span>
                <span className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
                  {headlineIsAqi ? 'AQI' : selShort}
                </span>
              </div>
              {showConcern && mainConcern && (
                <div className="mt-1 text-sm text-gray-400">
                  Main concern:{' '}
                  <span className="font-semibold text-gray-200">{mainConcern.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status + AQI scale */}
          {headlineIsAqi && (
            <div className="mt-3.5">
              <div
                className="mb-2 inline-flex items-center gap-1.5 text-base font-medium"
                style={{ color: isStale ? '#98A2B3' : accentHex }}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: isStale ? '#667085' : accentHex }}
                />
                {aqiStatus.label}
              </div>
              <div className="relative">
                <div className="flex h-2 overflow-hidden rounded-full">
                  {AQI_SEGMENTS.map((seg, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="flex-1" style={{ backgroundColor: seg.color }} />
                  ))}
                </div>
                {markerPercent !== null && (
                  <div
                    className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white/90 shadow-[0_0_0_2px_rgba(11,12,18,0.9)]"
                    style={{ left: `${markerPercent}%` }}
                  />
                )}
              </div>
              <div className="mt-1.5 flex justify-between text-sm font-medium text-gray-500">
                {[0, 50, 100, 150, 200, 300, 500].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
            </div>
          )}

          {(headlineIsAqi && aqiValue !== null) || isStale ? (
            <p className="mt-2.5 text-sm leading-snug text-gray-400">
              {summaryStale &&
                `No data in the selected period. Showing the last known readings · ${lastReportedLabel(
                  summary?.timeStamp
                )}.`}
              {!summaryStale &&
                isStale &&
                'This monitor has not reported recently — readings shown may be out of date.'}
              {!isStale && getAqiDescription(aqiValue as number)}
            </p>
          ) : null}
        </section>

        {/* ===== Individual Pollutant Readings ===== */}
        <h3 className="mb-2.5 mt-5 text-lg font-semibold text-white">
          Individual Pollutant Readings
        </h3>
        <div className="flex gap-2.5 overflow-x-auto px-0.5 pb-2 pt-1">
          {pollutantParameters.map((parameter) => {
            const isActive = parameter.parameterName === chartParameter.parameterName;
            const pVal = getReading(particleData[parameter.parameterName]);
            const pUnit = unitFor(parameter.parameterName);
            // Colour each reading by the same thresholds that drive the map
            // markers, so an out-of-range pollutant is obvious here rather than
            // only after opening its chart.
            const pStatus = pVal === null ? null : statusFor(parameter.parameterName, pVal);
            // Last known readings are drawn grey so they never pass for live ones.
            let pHex: string | null = pStatus
              ? BADGE_HEX[pStatus.badgeClass] || BADGE_HEX['bg-gray-500']
              : null;
            if (summaryStale) pHex = STALE_HEX;
            // The long-form explainer used to sit in a card of its own; it is a
            // hover tooltip now so it costs no vertical space.
            const pInfo = PARAM_INFO[parameter.parameterName];
            return (
              <button
                key={parameter.parameterName}
                type="button"
                onClick={() => setSelectedParameter(parameter.parameterName)}
                title={pInfo ? `${pInfo.full} — ${pInfo.desc}` : undefined}
                className={`${card} w-[8.25rem] shrink-0 px-4 py-3.5 text-left transition-all ${
                  isActive
                    ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 ring-1 ring-inset ring-[#8B5CF6]/30'
                    : 'hover:border-white/[0.14]'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: pHex || '#4B5563' }}
                  />
                  <span className="truncate text-base font-medium text-gray-400">
                    {getParameterName(parameter, deviceType).shortName}
                  </span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-1">
                  <span
                    className="text-3xl font-bold leading-none"
                    style={{ color: pHex || '#4B5563' }}
                  >
                    {pVal !== null ? getDisplayableValue(parameter.parameterName, pVal) : '--'}
                  </span>
                  {pVal !== null && pUnit ? (
                    <span className="truncate text-xs font-medium text-gray-500">{pUnit}</span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>

        {/* ===== Device Data ===== */}
        <h3 className="mb-3 mt-6 text-lg font-semibold text-white">Device Data</h3>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-base text-gray-400">Readings over time</p>
          <div className="relative flex rounded bg-white/[0.04] p-1 ring-1 ring-white/[0.06]">
            {RANGE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setChartRange(tab.id);
                  setShowRangePicker(false);
                }}
                className={`rounded px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  chartRange === tab.id
                    ? 'bg-[#7C5CFC] text-white shadow-[0_2px_8px_rgba(124,92,252,0.45)]'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
            {/* Custom range: opens an inline start/end date picker. */}
            <button
              type="button"
              onClick={() => setShowRangePicker((open) => !open)}
              className={`flex items-center gap-1 rounded px-3.5 py-1.5 text-sm font-medium transition-colors ${
                chartRange === 'custom'
                  ? 'bg-[#7C5CFC] text-white shadow-[0_2px_8px_rgba(124,92,252,0.45)]'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <span className="material-icons-outlined text-[17px]">date_range</span>
              {chartRange === 'custom' && customRange
                ? `${moment(customRange.startDate).format('DD MMM')} – ${moment(
                    customRange.endDate
                  ).format('DD MMM')}`
                : 'Custom'}
            </button>
            {showRangePicker && (
              <div className="absolute right-0 top-full z-20 mt-2">
                <DateRangePicker
                  timeSpanDate={customRange ?? chartTimeSpanDate}
                  onApply={(value) => {
                    setCustomRange(value);
                    setChartRange('custom');
                    setShowRangePicker(false);
                  }}
                  handleCancel={() => setShowRangePicker(false)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
          <div className="flex items-center gap-2">
            <span className={controlLabel}>Metric</span>
            <div className="relative">
              <select
                value={chartParameter.parameterName}
                onChange={(e) => setSelectedParameter(e.target.value as ParametersType)}
                className={selectClass}
              >
                {sortedParameters.map((p) => (
                  <option
                    key={p.parameterName}
                    value={p.parameterName}
                    className="bg-background-800"
                  >
                    {getParameterName(p, deviceType).displayName}
                  </option>
                ))}
              </select>
              <span className={chevron}>expand_more</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={controlLabel}>Resolution</span>
            <div className="relative">
              <select
                value={chartResolution}
                onChange={(e) => setResolution(e.target.value)}
                className={selectClass}
              >
                {getResolutionOptions(
                  chartTimeSpanDate.startDate,
                  chartTimeSpanDate.endDate
                ).map((option) => (
                  <option key={option.value} value={option.value} className="bg-background-800">
                    {option.label}
                  </option>
                ))}
              </select>
              <span className={chevron}>expand_more</span>
            </div>
          </div>
          {hasThresholdLines && (
            <button
              type="button"
              onClick={() => setShowThresholds((shown) => !shown)}
              aria-pressed={showThresholds}
              className={`${control} flex items-center gap-1.5 px-3 ${
                showThresholds
                  ? 'border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#A78BFA]'
                  : `${controlIdle} text-gray-400 hover:text-gray-200`
              }`}
            >
              <span className="material-icons-outlined text-[18px]">show_chart</span>
              Thresholds
            </button>
          )}
          <div className="ml-auto text-sm text-gray-400">
            Current{' '}
            <span className="ml-1 text-base font-semibold text-white">
              {chartValue !== null
                ? `${getDisplayableValue(chartParameter.parameterName, chartValue)}${
                    chartUnit ? ` ${chartUnit}` : ''
                  }`
                : '--'}
            </span>
          </div>
        </div>

        <div className={`${card} mt-4 p-4`}>
          <div className="h-[22rem]">{chartContent}</div>
        </div>

        <button
          type="button"
          onClick={downloadCsv}
          disabled={!series.length}
          className={`${card} mt-4 flex w-full items-center justify-center gap-2 py-3.5 text-base font-semibold text-[#A78BFA] transition-colors hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <span className="material-icons-outlined text-[20px]">download</span>
          Download {selShort} data (CSV)
        </button>
      </div>

      {/* Full-size photo. Fixed, so the panel's own overflow cannot clip it. */}
      {photoExpanded && photoUrl && (
        <div
          role="presentation"
          onClick={() => setPhotoExpanded(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm"
        >
          <img
            src={photoUrl}
            alt={`${device.deviceName} sensor`}
            className="max-h-[85vh] max-w-[90vw] rounded object-contain shadow-2xl"
          />
          <button
            type="button"
            aria-label="Close photo"
            onClick={() => setPhotoExpanded(false)}
            className="material-icons-outlined absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-gray-200 transition-colors hover:bg-white/20 hover:text-white"
          >
            close
          </button>
        </div>
      )}
    </SidePanelContainer>
  );
}
